import React, { useEffect } from "react";
import { Box, styled, Typography } from "@mui/material";
import { CustomDraggable } from "../../component/Draggable";
import { palette } from "../../themes/theme";
import { useSelector } from "react-redux";
import {
  crimeRiskLayer,
  environmentalRiskLayer,
  formatEnvRiskValue,
  getEnvRangeUpperBound,
} from "../../utils/helper";

const CrimeRiskLegend = ({ showLegend = false, leagendRef }) => {
  const legendData = useSelector((state) => state.map.shadingSettings);
  const activeCrimeType = useSelector((state) => state.map.activeCrimeType);
  const activeEnvType = useSelector((state) => state.map.activeEnvType);

  const [currentLayerType, setCurrentLayerType] = React.useState("");

  useEffect(() => {
    if (activeCrimeType) {
      const activeRisklayer = crimeRiskLayer.find(
        (layer) => layer.id === activeCrimeType
      );
      setCurrentLayerType(activeRisklayer.title);
    } else if (activeEnvType) {
      const activeEnvLayer = environmentalRiskLayer.find(
        (layer) => layer.id === activeEnvType
      );
      setCurrentLayerType(activeEnvLayer.title);
    } else {
      setCurrentLayerType("Legend");
    }
  }, [activeCrimeType, activeEnvType]);

  const { legendColorSchemeData, useGradings } = legendData;
  const isEnvironmentalLayer = Boolean(activeEnvType);

  const formatLegendValue = (value) =>
    isEnvironmentalLayer
      ? formatEnvRiskValue(value)
      : Number(value / 100).toFixed(2);

  const formatRangeBound = (value) =>
    isEnvironmentalLayer
      ? getEnvRangeUpperBound(value)
      : Number((value - 1) / 100).toFixed(2);

  return showLegend ? (
    <CustomDraggable
      isToolbox
      width={160}
      height={155}
      left={20}
      top={20}
      bounds={{
        left: 20,
      }}
      customComponent={
        <Box
          ref={leagendRef}
          sx={{
            width: 250,
            background: palette.background.light,
            borderRadius: "0px",
            position: "absolute",
            zIndex: 4,
            paddingBottom: "12px",
            border: `1px solid ${palette.primary.main}`,
          }}
        >
          <div
            style={{
              width: "100%",
              cursor: "all-scroll",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 0px",
            }}
            className="draggable-header"
          >
            <Typography
              sx={{
                color: palette.text.dark,
                fontWeight: "800",
                padding: "0 18px",
                textAlign: "center",
              }}
            >
              {currentLayerType}
              <br />
              {!isEnvironmentalLayer && (
                <span style={{ fontWeight: 500, fontSize: 12 }}>
                  (1.0 is National Average)
                </span>
              )}
            </Typography>
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 2,
              gap: "8px",
            }}
          >
            {legendColorSchemeData?.length > 0 &&
              legendColorSchemeData.map((item, index) => {
                let showRange = "";
                if (!useGradings) {
                  if (index === 0) {
                    showRange = `or More`;
                  } else if (index === legendColorSchemeData.length - 1) {
                    showRange = `or Less`;
                  } else {
                    showRange = `to ${formatRangeBound(
                      legendColorSchemeData[index - 1].value
                    )}`;
                  }
                }
                return (
                  <LegendItem key={index}>
                    <ColorBox color={item.color} />
                    <Label>
                      {useGradings
                        ? item.grade
                        : `${formatLegendValue(item.value)} ${showRange}${
                            isEnvironmentalLayer ? "" : " x Natl. Avg."
                          }`}
                    </Label>
                  </LegendItem>
                );
              })}
          </Box>
        </Box>
      }
    />
  ) : (
    <></>
  );
};

const LegendItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ColorBox = styled(Box)`
  width: 30px;
  height: 20px;
  border-radius: 4px;
  background-color: ${(props) => props.color || "#000"};
  border: 1px solid #ccc;
`;

const Label = styled(Typography)`
  font-size: 14px;
  color: ${(props) => props.theme.palette.text.dark};
`;

export default CrimeRiskLegend;
